'use client';
import { Show, Text } from '@chakra-ui/react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';
import slugify from 'react-slugify';

import GlobalSearch from '@/app/_components/search/GlobalSearch';
import { ReactComponent as KiaLogo } from '@/app/_icons/kia-logo-white.svg';
import { setDealershipCookieOnUser } from '@/services/gtm.service';
import { ShoppingToolsMenu } from '@/services/menu/shoppingtools.service';
import { BlogItem } from '@/types/blog/blog.types';
import { NewCar } from '@/types/new-cars/new-cars.types';

import { Hamburger } from './hamburger/Hamburger';
import FinancialServices from './megamenu/FinancialServices';
import PreOwned from './megamenu/PreOwned';
import Vehicles from './megamenu/Vehicles';
import styles from './Nav.module.scss';

const ShoppingTools = dynamic(
  () => import('./megamenu/ShoppingTools').then((module) => module),
  {
    ssr: false,
  },
);

function KiaHeader({
  config,
  newCars,
  latestBlogPosts,
  selectedDealershipName,
  shoppingToolsItems,
  isBuyOnlinePublished,
  salespersonSlug,
  dealershipSlug,
}: {
  config: {
    search_quick_links: {
      title: string;
      url: string;
    }[];
    new_car_mega_menu_disclaimer?: string | null;
  };
  newCars: NewCar[];
  latestBlogPosts: BlogItem[];
  selectedDealershipName?: string;
  shoppingToolsItems: ShoppingToolsMenu | null;
  isBuyOnlinePublished: boolean;
  salespersonSlug?: string | null;
  dealershipSlug?: string | null;
}) {
  const displayDealershipName = Boolean(selectedDealershipName);
  const pathname = usePathname();
  const isSalespersonPage = /^\/dealerships\/[^/]+\/[^/]+$/.test(pathname);
  const formatName = (slug: string): string => {
    if (!slug) return '';
    const parts = slug.split('-').filter(Boolean);

    if (parts.length === 0) return '';

    const lastPart = parts[parts.length - 1]?.toLowerCase();
    
    if (lastPart === 'dealership') {
      parts.pop();
    }

    return parts
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
      .trim();
  };

  useEffect(() => {
    setDealershipCookieOnUser();
  }, []);
  
  return (
    <nav className={styles.stickyNav}>
      {isSalespersonPage ? (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '83px',
          backgroundColor: '#05141f',
        }}
      >
        <a href="/" aria-label="kia home">
          <KiaLogo
            className={styles.micrositeLogo}
            width="80px"
            height="20px"
          />
        </a>
      </div>
    ) : (
      <div
        className={styles.mainNav}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <div
          className={styles.flexWrapper}
          style={{
            display: 'flex',
            justifyContent: 'space-around',
            maxWidth: '1440px',
            width: '100%',
            margin: '0 auto',
            height: '83px',
            alignItems: 'center',
          }}
        >
          <Show above="lg">
            <div className={styles.KiaOneNavigation}>
              <ul>
                <li className={`${styles.menuItem} ${styles.KiaOneMegamenu}`}>
                  <Vehicles
                    newCars={newCars}
                    selectedDealershipName={selectedDealershipName}
                    new_car_mega_menu_disclaimer={
                      config?.new_car_mega_menu_disclaimer
                    }
                  />
                </li>
                <li className={`${styles.menuItem} ${styles.KiaOneMegamenu}`}>
                  <PreOwned />
                </li>
                <li className={styles.menuItem}>
                  <Link
                    role="link"
                    className={styles.menuLink}
                    href="/promotions"
                  >
                    Promotions
                  </Link>
                </li>
                {isBuyOnlinePublished ? (
                  <li className={styles.menuItem}>
                    <Link
                      role="link"
                      className={styles.menuLink}
                      href="/buy-online"
                    >
                      Buy Online
                    </Link>
                  </li>
                ) : null}
              </ul>

              {salespersonSlug && dealershipSlug ? (
                // Salesperson logged in → link to their landing page
                <Link
                  role="link"
                  className={styles.logo}
                  href={`/dealerships/${dealershipSlug}/${salespersonSlug}`}
                >
                  <KiaLogo
                    className={styles.micrositeLogo}
                    width="80px"
                    height="20px"
                  />
                  <Text
                    fontSize="xs"
                    className={styles.micrositeName}
                    color="white"
                    marginTop={2}
                    textAlign="center"
                  >
                    {selectedDealershipName}
                  </Text>
                </Link>
              ) : displayDealershipName ? (
                // Dealership microsite → link to dealership page
                <Link
                  role="link"
                  className={styles.logo}
                  href={`/dealerships/${slugify(selectedDealershipName)}`}
                >
                  <KiaLogo
                    className={styles.micrositeLogo}
                    width="80px"
                    height="20px"
                  />
                  <Text
                    fontSize="xs"
                    className={styles.micrositeName}
                    color="white"
                    marginTop={2}
                    textAlign="center"
                  >
                    {selectedDealershipName}
                  </Text>
                </Link>
              ) : (
                // Default (public site)
                <Link
                  role="link"
                  aria-label="kia home"
                  className={styles.logo}
                  href="/"
                >
                  <KiaLogo width="80px" height="20px" />
                </Link>
              )}

              <ul
                style={{
                  margin: '-20px',
                }}
              >
                {salespersonSlug && dealershipSlug && (
                  <li className={styles.menuItem}>
                    <Link
                      role="link"
                      className={styles.menuLink}
                      href={`/dealerships/${dealershipSlug}/${salespersonSlug}`}
                    >
                      {formatName(salespersonSlug)}
                    </Link>
                  </li>
                )}
                <li className={`${styles.menuItem} ${styles.KiaOneMegamenu}`}>
                  <ShoppingTools shoppingToolsItems={shoppingToolsItems} />
                </li>
                <li className={`${styles.menuItem} ${styles.KiaOneMegamenu}`}>
                  <FinancialServices />
                </li>
                {!displayDealershipName && (
                  <li className={styles.menuItem}>
                    <Link
                      role="link"
                      className={styles.menuLink}
                      href="/find-a-dealer"
                      data-cy="find-a-dealer"
                    >
                      Find a Dealer
                    </Link>
                  </li>
                )}
              </ul>
            </div>
          </Show>
          <div className={styles.iconsWrapper}>
            {salespersonSlug && dealershipSlug ? (
              // Salesperson logged in → link to their landing page
              <Link
                role="link"
                className={styles.micorsiteLogoWrap}
                href={`/dealerships/${dealershipSlug}/${salespersonSlug}`}
              >
                <KiaLogo
                  className={styles.micrositeLogo}
                  width="80px"
                  height="20px"
                />
                <Text
                  fontSize="xs"
                  className={styles.micrositeName}
                  color={'white'}
                >
                  {selectedDealershipName}
                </Text>
              </Link>
              ) : (displayDealershipName ? (
                <Link
                  className={styles.micorsiteLogoWrap}
                  role="link"
                  href={`/dealerships/${slugify(selectedDealershipName)}`}
                >
                  <KiaLogo
                    className={styles.micrositeLogo}
                    width={'80px'}
                    height={'20px'}
                  />
                  <Text
                    fontSize="xs"
                    className={styles.micrositeName}
                    color={'white'}
                  >
                    {selectedDealershipName}
                  </Text>
                </Link>
            ) : (
              <Link
                href="/"
                role="link"
                className={styles.KiaLogo}
                aria-label="kia home"
              >
                <KiaLogo width={'80px'} height={'20px'} />
              </Link>
            ))}
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div className="headerGlobalSearchBtnMob">
                <GlobalSearch
                  config={config}
                  newCars={newCars}
                  latestBlogPosts={latestBlogPosts}
                />
              </div>
              <Hamburger
                selectedDealershipName={selectedDealershipName}
                newCars={newCars}
                shoppingToolsItems={shoppingToolsItems}
                new_car_mega_menu_disclaimer={
                  config?.new_car_mega_menu_disclaimer
                }
                isBuyOnlinePublished={isBuyOnlinePublished}
                salespersonSlug={salespersonSlug}
                dealershipSlug={dealershipSlug}
              />
            </div>
          </div>
        </div>
        <div className="headerGlobalSearchBtnDesk">
          <GlobalSearch
            config={config}
            newCars={newCars}
            latestBlogPosts={latestBlogPosts}
          />
        </div>
      </div>
    )}
   </nav>
  );
}

export default KiaHeader;
